import { z } from '@/utils/zod'

import { EntitySchema } from './Entity'
import {
    StorageResourceSchema,
    LanguageSchema,
    CountrySchema,
    ThemeColorsSchema,
    defaultLightThemeColors,
    defaultDarkThemeColors,
} from './common'
import { PresenterSchema } from './presenter'
import { LocalAuthProviderSchema, GoogleAuthProviderSchema, MicrosoftAuthProviderSchema } from './authProvider'
import { ScopeDisplaySchema, ScopeNameSchema } from './oauth'
import { ResourceServerSchema } from './ResourceServer'

export const TenantShortIdSchema = z.string().min(5).max(5)

const TenantEmailDomainDnsRecordSchema = z.object({
    type: z.string().min(1),
    name: z.string().min(1),
    value: z.string().min(1),
    verifiedAt: z.isoDateTime.nullable(),
})

export const TenantEmailDomainSchema = z.object({
    name: z.string().min(1),
    registeredAt: z.isoDateTime,
    checkedAt: z.isoDateTime.nullable(),
    verifiedAt: z.isoDateTime.nullable(),
    dnsRecords: z.array(TenantEmailDomainDnsRecordSchema),
    webhooks: z.object({
        registeredAt: z.isoDateTime.nullable(),
        error: z
            .object({
                message: z.string().min(1),
                occurredAt: z.isoDateTime,
            })
            .nullable(),
    }),
})

export type TenantEmailDomain = z.infer<typeof TenantEmailDomainSchema>

export const TenantEmailSettingsSchema = z.object({
    sender: z.object({
        name: z.string().min(1).max(120),
        localPart: z.string().min(1),
    }),
    replyTo: z.email().nullable(),
    domain: TenantEmailDomainSchema.nullable(),
})

export const TenantCustomDomainSchema = z.object({
    name: z.string().min(1),
    registeredAt: z.isoDateTime,
    checkedAt: z.isoDateTime.nullable(),
    dns: z.object({
        cname: z.object({
            name: z.string().min(1),
            value: z.string().min(1),
            verifiedAt: z.isoDateTime.nullable(),
        }),
        txt: z.object({
            name: z.string().min(1),
            value: z.string().min(1),
            verifiedAt: z.isoDateTime.nullable(),
        }),
    }),
    certificate: z.object({
        name: z.string().min(1),
        createdAt: z.isoDateTime.nullable(),
        readyAt: z.isoDateTime.nullable(),
        error: z
            .object({
                message: z.string().min(1),
                occurredAt: z.isoDateTime,
            })
            .nullable(),
    }),
    ingress: z.object({
        name: z.string().min(1),
        createdAt: z.isoDateTime.nullable(),
        error: z
            .object({
                message: z.string().min(1),
                occurredAt: z.isoDateTime,
            })
            .nullable(),
    }),
})

export type TenantCustomDomain = z.infer<typeof TenantCustomDomainSchema>

export const defaultTenantAccountAppearance = {
    favicon: null,
    darkModeEnabled: false,
    schemes: {
        light: {
            colors: { ...defaultLightThemeColors },
        },
        dark: {
            colors: { ...defaultDarkThemeColors },
        },
    },
} as const

export const TenantAccountAppearanceSchema = z.object({
    favicon: StorageResourceSchema.nullable(),
    darkModeEnabled: z.boolean(),
    schemes: z.object({
        light: z.object({
            colors: ThemeColorsSchema,
        }),
        dark: z.object({
            colors: ThemeColorsSchema,
        }),
    }),
})

export type TenantAccountAppearance = z.infer<typeof TenantAccountAppearanceSchema>

export const TenantAccountManagementSchema = z
    .object({
        enabled: z.boolean(),
        applicationId: z.objectId().nullable(),
        domain: TenantCustomDomainSchema.nullable(),
        appearance: TenantAccountAppearanceSchema,
    })
    .refine((settings) => !settings.enabled || settings.applicationId !== null, {
        message: 'An application is required when account management is enabled',
        path: ['applicationId'],
    })

export const ClaimSchema = z.string().min(1).max(32)

export type Claim = z.infer<typeof ClaimSchema>

export const ScopeSchema = z.object({
    name: ScopeNameSchema,
    claims: z.array(ClaimSchema).min(1),
    display: ScopeDisplaySchema,
})

export type Scope = z.infer<typeof ScopeSchema>

export const TenantSchema = EntitySchema.extend({
    shortId: TenantShortIdSchema,
    name: z.string().min(1).max(120),
    avatar: StorageResourceSchema.nullable(),
    slug: z.string().min(1),
    authDomain: TenantCustomDomainSchema.nullable(),

    email: TenantEmailSettingsSchema,

    country: CountrySchema,
    language: LanguageSchema,
    availableLanguages: z.array(LanguageSchema).min(1),

    accountManagement: TenantAccountManagementSchema,

    allowAuthTroughIframe: z.boolean(),
    allowImplicitFlow: z.boolean(),

    requireLogoutConfirmation: z.boolean(),

    signup: z.object({
        perIpLimit: z.toggleable({
            maxRegistrations: z.number().int().min(1),
            window: z.number().int().min(1), // seconds
        }),
    }),

    session: z.object({
        persistent: z.boolean(),
        idleTtl: z.number().min(0),
        maxTtl: z.number().min(0),
    }),

    scopes: z.array(ScopeSchema),
    additionalClaims: z.array(ClaimSchema),
    resources: z.array(ResourceServerSchema),

    cookies: z.object({
        keys: z.array(z.string().min(1)).min(1),
    }),

    cookiesSettings: z.object({
        autoRotate: z.boolean(),
        rotationInterval: z.number().min(1),
        maxKeys: z.number().min(1).max(10),
        nextRotationAt: z.isoDateTime.nullable(),
    }),

    signingKeysSettings: z.object({
        autoRotate: z.boolean(),
        rotationInterval: z.number().min(1),
        nextRotationAt: z.isoDateTime.nullable(),
    }),

    authProviders: z.array(
        z.discriminatedUnion('type', [LocalAuthProviderSchema, GoogleAuthProviderSchema, MicrosoftAuthProviderSchema])
    ),

    presenter: PresenterSchema,
}).superRefine((tenant, ctx) => {
    const resourceIds = tenant.resources.map((resource) => resource.id)
    const resourceIndicators = tenant.resources.map((resource) => resource.indicator)

    if (new Set(resourceIds).size !== resourceIds.length) {
        ctx.issues.push({
            code: 'custom',
            input: tenant.resources,
            path: ['resources'],
            message: 'Tenant resources must be unique by id',
        })
    }

    if (new Set(resourceIndicators).size !== resourceIndicators.length) {
        ctx.issues.push({
            code: 'custom',
            input: tenant.resources,
            path: ['resources'],
            message: 'Tenant resources must be unique by indicator',
        })
    }
})

export type Tenant = z.infer<typeof TenantSchema>
